import { ExcelButton } from "@/controls/buttons/ExcelButton";
import { ResetFiltersButton } from "@/controls/buttons/ResetFiltersButton";
import { Autocomplete, Box, MenuItem, TextField } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import type { Moment } from "moment";
import type {
	BilletRFactorPickerBilletSize,
	BilletRFactorPickerCrew,
	BilletRFactorPickerProductSize,
	BilletRFactorPickerProductType,
} from "../models/BilletsRFactor";

const SELECT_SLOT_PROPS = {
	inputLabel: { shrink: true },
	select: { displayEmpty: true },
};

interface Props {
	start: Moment;
	end: Moment;
	productTypeId: number | null;
	productSizeId: number | null;
	crewId: string | null;
	billetSizeId: number | null;
	productTypes: BilletRFactorPickerProductType[];
	productSizes: BilletRFactorPickerProductSize[];
	crews: BilletRFactorPickerCrew[];
	billetSizes: BilletRFactorPickerBilletSize[];
	onStartChange: (date: Moment | null) => void;
	onEndChange: (date: Moment | null) => void;
	onProductTypeChange: (id: number | null) => void;
	onProductSizeChange: (id: number | null) => void;
	onCrewChange: (crew: string | null) => void;
	onBilletSizeChange: (id: number | null) => void;
	onReset: () => void;
	canReset: boolean;
	onExportExcel: () => void;
	canExport: boolean;
}

// Same filters, labels and order as the legacy screen.
export const BilletsRFactorToolbar = ({
	start,
	end,
	productTypeId,
	productSizeId,
	crewId,
	billetSizeId,
	productTypes,
	productSizes,
	crews,
	billetSizes,
	onStartChange,
	onEndChange,
	onProductTypeChange,
	onProductSizeChange,
	onCrewChange,
	onBilletSizeChange,
	onReset,
	canReset,
	onExportExcel,
	canExport,
}: Props) => (
	<Box
		sx={{
			display: "flex",
			alignItems: "center",
			gap: 1,
			px: 1.5,
			py: 0.75,
			flexWrap: "wrap",
		}}
	>
		<Box sx={{ flex: "0 0 150px" }}>
			<DatePicker
				label="Start"
				format="MM/DD/YYYY"
				value={start}
				maxDate={end}
				onChange={onStartChange}
				slotProps={{ textField: { size: "small", fullWidth: true } }}
			/>
		</Box>
		<Box sx={{ flex: "0 0 150px" }}>
			<DatePicker
				label="End"
				format="MM/DD/YYYY"
				value={end}
				minDate={start}
				onChange={onEndChange}
				slotProps={{ textField: { size: "small", fullWidth: true } }}
			/>
		</Box>

		<TextField
			select
			size="small"
			label="Prod Type"
			slotProps={SELECT_SLOT_PROPS}
			sx={{ flex: "0 0 170px" }}
			value={productTypeId ?? ""}
			onChange={(e) =>
				onProductTypeChange(
					e.target.value === "" ? null : Number(e.target.value),
				)
			}
		>
			<MenuItem value="">All</MenuItem>
			{productTypes.map((type) => (
				<MenuItem key={type.Id} value={type.Id}>
					{type.Name}
				</MenuItem>
			))}
		</TextField>

		<Autocomplete
			size="small"
			sx={{ flex: "0 0 230px" }}
			options={productSizes}
			value={productSizes.find((s) => s.Id === productSizeId) ?? null}
			getOptionLabel={(option) => option.Name}
			isOptionEqualToValue={(option, selected) => option.Id === selected.Id}
			onChange={(_event, option) => onProductSizeChange(option?.Id ?? null)}
			autoHighlight
			renderOption={(props, option) => {
				const { key, ...rest } = props as typeof props & { key: string };
				return (
					<li key={option.Id} {...rest}>
						{option.Name}
					</li>
				);
			}}
			renderInput={(params) => (
				<TextField
					{...params}
					label="Product"
					slotProps={{ inputLabel: { shrink: true } }}
					placeholder="All"
				/>
			)}
		/>

		<TextField
			select
			size="small"
			label="Shift"
			slotProps={SELECT_SLOT_PROPS}
			sx={{ flex: "0 0 100px" }}
			value={crewId ?? ""}
			onChange={(e) =>
				onCrewChange(e.target.value === "" ? null : String(e.target.value))
			}
		>
			<MenuItem value="">All</MenuItem>
			{crews.map((crew) => (
				<MenuItem key={crew.Id} value={crew.Id}>
					{crew.Id}
				</MenuItem>
			))}
		</TextField>

		<TextField
			select
			size="small"
			label="Billet Size"
			slotProps={SELECT_SLOT_PROPS}
			sx={{ flex: "0 0 120px" }}
			value={billetSizeId ?? ""}
			onChange={(e) =>
				onBilletSizeChange(
					e.target.value === "" ? null : Number(e.target.value),
				)
			}
		>
			<MenuItem value="">All</MenuItem>
			{billetSizes.map((size) => (
				<MenuItem key={size.Id} value={size.Id}>
					{size.Name}
				</MenuItem>
			))}
		</TextField>

		<ResetFiltersButton onClick={onReset} disabled={!canReset} />

		<Box sx={{ flexGrow: 1, minWidth: 8 }} />

		<Box sx={{ width: 200 }}>
			<ExcelButton handleExportToExcel={onExportExcel} disabled={!canExport} />
		</Box>
	</Box>
);
